import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { candidateAPI, favoritesAPI, hiresAPI, matchingAPI, hotelAPI, paymentsAPI, authAPI, messagesAPI } from "@/lib/api";
import { useTranslation, useLanguage, POSITIONS_LIST, EUROPEAN_COUNTRIES, COUNTRY_FLAGS, PRIORITY_COUNTRIES, COUNTRY_NAMES } from "@/lib/i18n";
import { LanguageSelector } from "@/components/shared/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Logo } from "@/components/shared/Logo";
import { MessagesView } from "@/components/MessagesView";
import { SettingsView } from "@/components/shared/SettingsView";
import { JobPostingsView } from "@/components/JobPostingsView";
import {
  Search, Heart, Users, Zap, LogOut, MapPin, Briefcase,
  CheckCircle2, Crown, Loader2, UserCheck, Lock, FileText, Download, Shield, Home, AlertTriangle, Trash2, Building2, MessageSquare, ShieldCheck, Clock, Navigation, Eye, Video, Settings, CalendarDays, Menu
} from "lucide-react";
import { toast } from "sonner";

function LockedFeature({ icon: Icon, title, description, onUpgrade, t }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="locked-feature">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Lock className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="font-sans font-semibold text-lg text-slate-900 mb-2">{title}</h3>
      <p className="text-sm text-slate-500 font-body mb-6 max-w-sm">{description}</p>
      <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm" onClick={onUpgrade} data-testid="upgrade-plan-btn">
        <Crown className="w-4 h-4 mr-2" /> {t("hd.upgrade_for_feature")}
      </Button>
    </div>
  );
}

function CandidateCard({ candidate: initialCandidate, isFavorite, canFavorite, canViewCV, onFavorite, onHire, onRemoveFavorite, isFree, onFreeClick, t, tp, tc, tl, onMessage, canMessage, profileViewsRemaining, contactsRemaining, onProfileViewed, onUpgrade, onContactsExhausted }) {
  const [showDetail, setShowDetail] = useState(false);
  const [candidate, setCandidate] = useState(initialCandidate);
  const [unlocking, setUnlocking] = useState(false);
  const isDistanceRestricted = initialCandidate.distance_restricted;
  // Sync when parent data changes (e.g. new search results with distance)
  useEffect(() => { setCandidate(initialCandidate); }, [initialCandidate]);
  const handleClick = async () => {
    if (isDistanceRestricted) { if (onUpgrade) onUpgrade(); return; }
    if (isFree && profileViewsRemaining <= 0) { onFreeClick(); return; }
    if (isFree) {
      try {
        // Open profile WITHOUT consuming a contact (view_contacts=false)
        const res = await candidateAPI.getById(initialCandidate.user_id, false);
        setCandidate(res.data);
        if (onProfileViewed) onProfileViewed();
      } catch (err) {
        if (err.response?.status === 403) { onFreeClick(); return; }
      }
    }
    setShowDetail(true);
  };
  const handleUnlockContacts = async () => {
    setUnlocking(true);
    try {
      const res = await candidateAPI.getById(initialCandidate.user_id, true);
      setCandidate(res.data);
      if (onProfileViewed) onProfileViewed();
    } catch (err) {
      if (err.response?.status === 403) {
        if (onContactsExhausted) onContactsExhausted();
      }
    } finally {
      setUnlocking(false);
    }
  };
  return (
    <>
      <Card className={`bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${isDistanceRestricted ? "relative overflow-hidden" : ""}`} onClick={handleClick} data-testid={`candidate-card-${candidate.user_id}`}>
        {isDistanceRestricted && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center" data-testid={`distance-restricted-${candidate.user_id}`}>
            <div className="text-center px-4">
              <Lock className="w-6 h-6 text-slate-400 mx-auto mb-1.5" />
              <p className="text-xs font-body text-slate-500 font-semibold">{t("dist.restricted_short")}</p>
            </div>
          </div>
        )}
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            {candidate.photo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${candidate.photo_url}`} alt="" className="w-12 h-12 rounded-full object-cover flex-shrink-0" data-testid={`candidate-photo-${candidate.user_id}`} />
            ) : (
              <div className="w-12 h-12 rounded-full bg-navy-900 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-sans font-bold text-sm">{(candidate.name || "?")[0]?.toUpperCase()}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-sans font-semibold text-slate-900 truncate">{candidate.name || "?"}</h3>
                {candidate.is_premium && <Crown className="w-4 h-4 text-gold-500" />}
                {candidate.available_now && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-body">{t("hd.available")}</Badge>}
                {!candidate.available_now && candidate.available_from && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs font-body"><CalendarDays className="w-3 h-3 mr-1 inline" />{candidate.available_from}</Badge>}
                {candidate.ai_score && <Badge className="bg-gold-500/10 text-gold-600 border-gold-500/20 text-xs font-body">Score: {candidate.ai_score}</Badge>}
                {candidate.distance_km !== undefined && candidate.distance_km !== null && (
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs font-body" data-testid={`distance-badge-${candidate.user_id}`}>
                    <Navigation className="w-3 h-3 mr-1" />{candidate.distance_km} {t("dist.km")}
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-slate-500 font-body mb-2">
                {candidate.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{candidate.location}</span>}
                {candidate.country && <span className="flex items-center gap-1">{COUNTRY_FLAGS[candidate.country] || ""} {tc(candidate.country)}</span>}
                {candidate.experience_years != null && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{candidate.experience_years} {t("hd.years")}</span>}
                {candidate.expected_salary != null && <span>{candidate.expected_salary} EUR</span>}
                {candidate.has_cv && <span className="flex items-center gap-1 text-gold-600"><FileText className="w-3 h-3" />CV</span>}
                {candidate.video_url && <span className="flex items-center gap-1 text-sky-600"><Video className="w-3 h-3" />{t("hd.has_video")}</span>}
                {candidate.needs_accommodation && <span className="flex items-center gap-1 text-blue-600"><Home className="w-3 h-3" />{t("cd.needs_accommodation")}</span>}
              </div>
              {/* Preferred countries flags */}
              {(candidate.preferred_countries || []).length > 0 && (
                <div className="flex items-center gap-1.5 mb-2" data-testid={`pref-countries-${candidate.user_id}`}>
                  <span className="text-xs text-slate-400 font-body">{t("hd.preferred_countries")}:</span>
                  {candidate.preferred_countries.map(c => (
                    <span key={c} className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5 text-xs font-body">
                      <span>{COUNTRY_FLAGS[c] || ""}</span> {tc(c)}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-1">
                {(candidate.desired_positions || []).slice(0, 3).map(p => (
                  <Badge key={p} variant="outline" className="text-xs font-body border-slate-200">{tp(p)}</Badge>
                ))}
                {(candidate.desired_positions || []).length > 3 && <Badge variant="outline" className="text-xs font-body border-slate-200">+{candidate.desired_positions.length - 3}</Badge>}
              </div>
              {candidate.ai_reason && <p className="text-xs text-gold-600 font-body mt-2 italic">{candidate.ai_reason}</p>}
            </div>
            <div className="flex flex-col gap-1">
              {canFavorite && (
                <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${isFavorite ? "text-red-500" : "text-slate-400"}`}
                  onClick={(e) => { e.stopPropagation(); isFavorite ? onRemoveFavorite(candidate.user_id) : onFavorite(candidate.user_id); }}
                  data-testid={`fav-btn-${candidate.user_id}`}>
                  <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans text-xl flex items-center gap-3">
              {candidate.photo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${candidate.photo_url}`} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-navy-900 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-sans font-bold text-sm">{(candidate.name || "?")[0]?.toUpperCase()}</span>
                </div>
              )}
              <span className="flex items-center gap-2">{candidate.name} {candidate.is_premium && <Crown className="w-5 h-5 text-gold-500" />}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 font-body">
            <div className="grid grid-cols-2 gap-3 text-sm">
              {candidate.contact_restricted ? (
                <>
                  {candidate.contacts_exhausted ? (
                    <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-700 text-sm">
                      <Lock className="w-4 h-4 flex-shrink-0" /> {t("hd.free_contacts_exhausted")}
                    </div>
                  ) : candidate.verification_restricted ? (
                    <div className="col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-700 text-sm">
                      <Lock className="w-4 h-4 flex-shrink-0" /> {t("verif.contact_restricted")}
                    </div>
                  ) : (
                    <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="unlock-contacts-section">
                      <p className="text-sm text-blue-700 font-body mb-3">{t("hd.unlock_contacts_desc")} ({candidate.contacts_remaining || 0} {t("hd.unlock_remaining")})</p>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white w-full" disabled={unlocking}
                        onClick={(e) => { e.stopPropagation(); handleUnlockContacts(); }} data-testid="unlock-contacts-btn">
                        {unlocking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                        {t("hd.unlock_contacts_btn")}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div><span className="text-slate-500">{t("auth.email")}:</span> <span className="text-slate-900">{candidate.email}</span></div>
                  <div><span className="text-slate-500">{t("cd.phone")}:</span> <span className="text-slate-900">{candidate.phone || t("common.na")}</span></div>
                </>
              )}
              <div><span className="text-slate-500">{t("cd.location")}:</span> <span className="text-slate-900">{candidate.location || t("common.na")}</span></div>
              <div><span className="text-slate-500">{t("cd.country")}:</span> <span className="text-slate-900">{candidate.country ? tc(candidate.country) : t("common.na")}</span></div>
              <div><span className="text-slate-500">{t("cd.nationality")}:</span> <span className="text-slate-900">{candidate.nationality || t("common.na")}</span></div>
              <div><span className="text-slate-500">{t("cd.experience")}:</span> <span className="text-slate-900">{candidate.experience_years || 0} {t("hd.years")}</span></div>
              <div><span className="text-slate-500">{t("cd.salary")}:</span> <span className="text-slate-900">{candidate.expected_salary ? `${candidate.expected_salary} EUR` : t("common.na")}</span></div>
              <div><span className="text-slate-500">{t("cd.available_title")}:</span> <span className={candidate.available_now ? "text-emerald-600" : "text-slate-900"}>{candidate.available_now ? t("common.yes") : t("common.no")}</span>{!candidate.available_now && candidate.available_from && <span className="ml-2 text-amber-600 text-xs">({t("cd.available_from")}: {candidate.available_from})</span>}</div>
              <div><span className="text-slate-500">{t("cd.relocate")}:</span> <span className="text-slate-900">{candidate.willing_to_relocate ? t("common.yes") : t("common.no")}</span></div>
              <div><span className="text-slate-500">{t("cd.needs_accommodation")}:</span> <span className={candidate.needs_accommodation ? "text-blue-600 font-semibold" : "text-slate-900"}>{candidate.needs_accommodation ? t("common.yes") : t("common.no")}</span></div>
            </div>
            {/* Preferred countries in detail */}
            {(candidate.preferred_countries || []).length > 0 && (
              <div data-testid="detail-preferred-countries">
                <span className="text-sm text-slate-500 font-body">{t("hd.preferred_countries")}:</span>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {candidate.preferred_countries.map(c => (
                    <span key={c} className="inline-flex items-center gap-1.5 bg-sky-50 text-sky-800 border border-sky-200 rounded-full px-2.5 py-1 text-sm font-body">
                      <span>{COUNTRY_FLAGS[c] || ""}</span> {tc(c)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {candidate.bio && <p className="text-sm text-slate-600">{candidate.bio}</p>}

            {/* CV Section */}
            {candidate.has_cv && canViewCV && !candidate.contacts_exhausted && (
              <div className="bg-gold-50 border border-gold-200 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gold-600" />
                  <span className="text-sm font-semibold text-gold-800">{t("cd.cv_title")}</span>
                </div>
                <Button size="sm" variant="outline" className="border-gold-300 text-gold-700 hover:bg-gold-100"
                  onClick={async () => {
                    try {
                      const res = await candidateAPI.downloadCV(candidate.user_id);
                      const blob = new Blob([res.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      // Open in new tab so user can view the PDF
                      window.open(url, '_blank');
                      // Also trigger download
                      const a = document.createElement('a');
                      a.style.display = 'none';
                      a.href = url;
                      a.download = candidate.cv_original_name || `CV_${candidate.name || 'candidato'}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(url); }, 1000);
                    } catch (err) {
                      console.error("CV download failed:", err.response?.status);
                      if (err.response?.status === 403 && onContactsExhausted) { onContactsExhausted(); return; }
                      let detail = null;
                      try {
                        if (err.response?.data instanceof Blob) {
                          const text = await err.response.data.text();
                          detail = JSON.parse(text).detail;
                        } else {
                          detail = err.response?.data?.detail;
                        }
                      } catch {}
                      toast.error(detail || t("hd.cv_error"));
                    }
                  }} data-testid={`view-cv-${candidate.user_id}`}>
                  <Download className="w-3 h-3 mr-1" /> {t("hd.download_cv")}
                </Button>
              </div>
            )}
            {candidate.has_cv && (!canViewCV || candidate.contacts_exhausted) && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2 text-slate-500 text-sm">
                <Lock className="w-4 h-4" /> CV - {candidate.contacts_exhausted ? t("hd.free_contacts_exhausted_short") : t("hd.upgrade_for_feature")}
              </div>
            )}

            {/* Video Presentation */}
            {candidate.video_url && (
              <div data-testid="detail-video-section">
                <p className="text-sm text-slate-500 mb-2 flex items-center gap-1.5"><Video className="w-4 h-4" /> {t("cd.video_title")}:</p>
                <div className="rounded-lg overflow-hidden border border-slate-200 bg-black">
                  <video
                    src={`${candidateAPI.getVideoUrl(candidate.user_id)}?t=${Date.now()}`}
                    controls
                    className="w-full max-h-[240px]"
                    data-testid={`video-player-${candidate.user_id}`}
                  />
                </div>
              </div>
            )}

            <div>
              <p className="text-sm text-slate-500 mb-2">{t("cd.languages")}:</p>
              <div className="flex flex-wrap gap-2">
                {(candidate.languages || []).map((l) => <Badge key={`${l.language}-${l.level}`} variant="outline" className="font-body">{tl(l.language, l.level)}</Badge>)}
                {(!candidate.languages || candidate.languages.length === 0) && <span className="text-sm text-slate-400">{t("common.not_specified")}</span>}
              </div>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-2">{t("cd.positions")}:</p>
              <div className="flex flex-wrap gap-2">
                {(candidate.desired_positions || []).map(p => <Badge key={p} className="bg-gold-500/10 text-gold-600 border-gold-500/20 font-body">{tp(p)}</Badge>)}
              </div>
            </div>
            <Separator />
            <div className="flex gap-2">
              <Select onValueChange={(pos) => onHire(candidate.user_id, pos)}>
                <SelectTrigger className="flex-1 font-body" data-testid={`hire-select-${candidate.user_id}`}><SelectValue placeholder={t("hd.hire_as")} /></SelectTrigger>
                <SelectContent>
                  {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{tp(p)}</SelectItem>)}
                </SelectContent>
              </Select>
              {canMessage ? (
                <Button variant="outline" className="border-gold-300 text-gold-700 hover:bg-gold-50"
                  onClick={(e) => { e.stopPropagation(); onMessage(candidate.user_id); setShowDetail(false); }}
                  data-testid={`msg-btn-${candidate.user_id}`}>
                  <MessageSquare className="w-4 h-4" />
                </Button>
              ) : (
                <Button variant="outline" className="border-slate-200 text-slate-400 cursor-not-allowed" disabled
                  data-testid={`msg-btn-locked-${candidate.user_id}`}>
                  <Lock className="w-3 h-3 mr-1" /><MessageSquare className="w-4 h-4" />
                </Button>
              )}
              {canFavorite && (
                <Button variant="ghost" className={isFavorite ? "text-red-500" : "text-slate-400"}
                  onClick={() => isFavorite ? onRemoveFavorite(candidate.user_id) : onFavorite(candidate.user_id)}>
                  <Heart className={`w-5 h-5 ${isFavorite ? "fill-current" : ""}`} />
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function HotelDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, tp, tc, tl } = useTranslation();
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState("search");
  const [hotel, setHotel] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [hires, setHires] = useState([]);
  const [matchResults, setMatchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [filters, setFilters] = useState({ position: "", location: "", country: "", preferred_country: "", available_now: false, needs_accommodation: false, experience_min: "", available_from: "" });
  const [matchFilters, setMatchFilters] = useState({ positions: [], available_now: true });
  const [distanceFrom, setDistanceFrom] = useState("");
  const [distanceKm, setDistanceKm] = useState(100);
  const [distanceActive, setDistanceActive] = useState(false);
  const [geoSuggestions, setGeoSuggestions] = useState([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [distanceAccess, setDistanceAccess] = useState(true);
  const [distanceCoords, setDistanceCoords] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasSearched, setHasSearched] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [showMobileMore, setShowMobileMore] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [blockedInteractions, setBlockedInteractions] = useState(0);
  const [founderPromo, setFounderPromo] = useState(null);

  const features = planStatus?.features || {};
  const isFree = planStatus?.is_free || false;
  const isFounder = planStatus?.is_founder || false;
  const hasActivePlan = !isFree && planStatus?.status === "active" && planStatus?.plan;
  const profileViewsUsed = planStatus?.profile_views_used || 0;
  const profileViewsLimit = planStatus?.profile_views_limit || 10;
  const profileViewsRemaining = Math.max(0, profileViewsLimit - profileViewsUsed);
  const contactsUsed = planStatus?.contacts_used || 0;
  const contactsLimit = planStatus?.contacts_limit || 0;
  const contactsRemaining = contactsLimit > 0 ? Math.max(0, contactsLimit - contactsUsed) : (contactsLimit === -1 ? Infinity : 0);
  const freeCanViewCV = isFree && contactsRemaining > 0;
  const verificationStatus = planStatus?.verification_status || hotel?.verification_status || "pending";
  const isVerified = verificationStatus === "verified";

  const handleBlockedAction = (featureName) => {
    const newCount = blockedInteractions + 1;
    setBlockedInteractions(newCount);
    if (newCount >= 5) {
      setShowUpgradePopup(true);
    } else {
      toast.error(t("hd.free_feature_blocked", { feature: featureName }), { duration: 4000 });
    }
  };

  const NAV_ITEMS = [
    { id: "search", icon: Search, label: t("hd.search") },
    { id: "jobs", icon: Briefcase, label: t("hd.jobs") || "Jobs" },
    { id: "messages", icon: MessageSquare, label: t("msg.title"), locked: !features.messaging },
    { id: "favorites", icon: Heart, label: t("hd.favorites"), locked: !features.favorites },
    { id: "hired", icon: UserCheck, label: t("hd.hired") },
    { id: "matching", icon: Zap, label: t("hd.matching"), locked: !features.ai_matching },
    { id: "subscription", icon: Crown, label: t("hd.subscription") },
    { id: "settings", icon: Settings, label: t("settings.title") },
  ];

  useEffect(() => { loadHotel(); loadPlanStatus(); loadFavorites(); loadHires(); loadFounderPromo(); }, []);

  // Auto-load candidates on first render once planStatus is ready
  useEffect(() => {
    if (planStatus && !hasSearched) {
      searchCandidates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planStatus]);

  // Auto-search when filters change (debounced)
  useEffect(() => {
    if (!hasSearched) return;
    const timeout = setTimeout(() => { searchCandidates(); }, 350);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.position, filters.country, filters.preferred_country, filters.available_now, filters.needs_accommodation, filters.experience_min, filters.available_from]);

  // Auto-search when distance filter changes
  useEffect(() => {
    if (!hasSearched) return;
    if (!distanceActive) {
      // Toggled off - re-search without distance
      searchCandidates();
      return;
    }
    if (distanceActive && distanceFrom) {
      const timeout = setTimeout(() => { searchCandidates(); }, 500);
      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distanceActive, distanceKm]);

  // Dedicated effect for distanceFrom (selected location)
  const [selectedLocation, setSelectedLocation] = useState("");
  useEffect(() => {
    if (!hasSearched || !distanceActive || !selectedLocation) return;
    searchCandidates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation]);

  const loadHotel = async () => {
    try {
      const res = await hotelAPI.getProfile();
      setHotel(res.data);
      if (!res.data.onboarding_completed) navigate("/hotel/onboarding");
    } catch (err) { console.error("loadHotel failed:", err.message); }
  };
  const loadPlanStatus = async () => {
    try {
      const res = await hotelAPI.getPlanStatus();
      setPlanStatus(res.data);
    } catch (err) { console.error("loadPlanStatus failed:", err.message); }
  };
  const loadFounderPromo = async () => {
    try {
      const res = await paymentsAPI.getFounderPromo();
      setFounderPromo(res.data);
    } catch (err) { console.error("loadFounderPromo failed:", err.message); }
  };
  const loadFavorites = async () => {
    try {
      const res = await favoritesAPI.getAll();
      setFavorites(res.data);
      setFavoriteIds(new Set(res.data.map(f => f.candidate_user_id)));
    } catch (err) { console.error("loadFavorites failed:", err.message); }
  };
  const loadHires = async () => { try { const res = await hiresAPI.getAll(); setHires(res.data); } catch (err) { console.error("loadHires failed:", err.message); } };

  const searchCandidates = async (append = false, pageOverride = null) => {
    setSearchLoading(true);
    try {
      const params = { page: pageOverride || (append ? currentPage + 1 : 1), limit: 20 };
      if (filters.position) params.position = filters.position;
      if (filters.location && !isFree) params.location = filters.location;
      if (filters.country) params.country = filters.country;
      if (filters.preferred_country) params.preferred_country = filters.preferred_country;
      if (features.search_advanced) {
        if (filters.available_now) params.available_now = true;
        if (filters.needs_accommodation) params.needs_accommodation = true;
        if (filters.experience_min) params.experience_min = parseInt(filters.experience_min);
        if (filters.available_from) params.available_from = filters.available_from;
      }
      if (distanceActive && distanceFrom) {
        params.distance_from = distanceFrom;
        params.distance_km = distanceKm;
        if (distanceCoords) {
          params.distance_lat = distanceCoords.lat;
          params.distance_lng = distanceCoords.lng;
        }
      }
      const res = await candidateAPI.search(params);
      const newCandidates = res.data.candidates || [];
      if (append) {
        setCandidates(prev => [...prev, ...newCandidates]);
        setCurrentPage(params.page);
      } else {
        setCandidates(newCandidates);
        setCurrentPage(1);
      }
      setTotalResults(res.data.total || 0);
      setDistanceAccess(res.data.distance_access !== false);
      if (res.data.geocode_error) {
        toast.error(t("dist.geocode_error"));
      }
      setHasSearched(true);
    } catch (err) {
      const msg = err.response?.data?.detail || "Error";
      toast.error(msg);
    } finally { setSearchLoading(false); }
  };

  const searchGeoLocations = async (query) => {
    if (query.length < 2) { setGeoSuggestions([]); return; }
    setGeoLoading(true);
    try {
      const res = await candidateAPI.geocodeSearch(query);
      setGeoSuggestions(res.data.results || []);
    } catch (err) { console.error("searchGeo failed:", err.message); } finally { setGeoLoading(false); }
  };

  let geoTimeout = null;
  const handleDistanceFromChange = (val) => {
    setDistanceFrom(val);
    setDistanceCoords(null);
    clearTimeout(geoTimeout);
    geoTimeout = setTimeout(() => searchGeoLocations(val), 400);
  };

  const handleFavorite = async (userId) => {
    if (!features.favorites) { toast.error(t("hd.favorites_locked")); return; }
    try { await favoritesAPI.add(userId); toast.success("OK"); loadFavorites(); } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const handleRemoveFavorite = async (userId) => { try { await favoritesAPI.remove(userId); toast.success("OK"); loadFavorites(); } catch { toast.error("Error"); } };
  const handleHire = async (userId, position) => { try { await hiresAPI.create({ candidate_user_id: userId, position }); toast.success("OK"); loadHires(); } catch { toast.error("Error"); } };
  const runMatching = async () => {
    setLoading(true);
    try {
      const res = await matchingAPI.getRecommendations({
        positions: matchFilters.positions.length > 0 ? matchFilters.positions : (hotel?.positions_needed || []),
        available_now: matchFilters.available_now,
        ui_language: language,
      });
      setMatchResults(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); } finally { setLoading(false); }
  };
  const [showComingSoon, setShowComingSoon] = useState(false);
  const handleSubscription = () => setShowComingSoon(true);
  const goToSubscription = () => setActiveTab("subscription");

  const handleMessage = async (candidateUserId) => {
    if (isFree) {
      handleBlockedAction(t("msg.title"));
      return;
    }
    try {
      await messagesAPI.createConversation(candidateUserId);
      setActiveTab("messages");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="hotel-dashboard">
      <aside className="w-64 bg-navy-900 border-r border-navy-800 min-h-screen hidden lg:flex flex-col">
        <div className="flex items-center gap-2 px-6 h-16 border-b border-navy-800">
          <Logo size="md" className="text-white" />
        </div>
        <div className="px-4 pt-3 pb-1">
          <LanguageSelector variant="dark" />
        </div>
        {/* Free plan badge */}
        {isFree && (
          <div className="px-4 py-2">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-center">
              <span className="text-xs text-amber-400 font-semibold font-body">{t("hd.free_badge")}</span>
            </div>
          </div>
        )}
        {/* Founder badge */}
        {isFounder && !isFree && (
          <div className="px-4 py-2" data-testid="sidebar-founder-badge">
            <div className="bg-gradient-to-r from-amber-500/20 to-gold-500/20 border border-amber-500/40 rounded-lg p-2 text-center">
              <span className="text-xs text-amber-300 font-bold font-body">{t("hd.founder_badge")}</span>
              <p className="text-[10px] text-amber-400/70 font-body mt-0.5">99 EUR{t("price.month")} {t("hd.founder_lifetime")}</p>
            </div>
          </div>
        )}
        {/* Verification status badge */}
        {isVerified ? (
          <div className="px-4 py-2" data-testid="sidebar-verified-badge">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2 text-center flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-semibold font-body">{t("verif.badge")}</span>
            </div>
          </div>
        ) : (
          <div className="px-4 py-2" data-testid="sidebar-pending-badge">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 text-center flex items-center justify-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs text-amber-400 font-semibold font-body">{t("verif.pending")}</span>
            </div>
          </div>
        )}
        {/* Profile views counter - free plan */}
        {isFree && planStatus && (
          <div className="px-4 py-2">
            <div className="bg-navy-800 rounded-lg p-3">
              <div className="flex justify-between text-xs text-slate-400 font-body mb-1">
                <span>{profileViewsUsed}/{profileViewsLimit} {t("hd.free_views_used")}</span>
              </div>
              <div className="w-full bg-navy-700 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full transition-all ${profileViewsUsed >= profileViewsLimit ? "bg-red-500" : "bg-gold-500"}`}
                  style={{ width: `${Math.min(100, (profileViewsUsed / profileViewsLimit) * 100)}%` }} />
              </div>
              {profileViewsRemaining <= 3 && profileViewsRemaining > 0 && (
                <p className="text-xs text-amber-400 font-body mt-1">{t("hd.free_views_warning")}</p>
              )}
              {profileViewsRemaining === 0 && (
                <p className="text-xs text-red-400 font-body mt-1">{t("hd.free_views_exhausted")}</p>
              )}
            </div>
          </div>
        )}
        {/* Contacts counter - free plan */}
        {isFree && planStatus && contactsLimit > 0 && (
          <div className="px-4 py-1">
            <div className="bg-navy-800 rounded-lg p-3">
              <div className="flex justify-between text-xs text-slate-400 font-body mb-1">
                <span>{contactsUsed}/{contactsLimit} {t("hd.free_contacts_used")}</span>
              </div>
              <div className="w-full bg-navy-700 rounded-full h-1.5">
                <div className={`h-1.5 rounded-full transition-all ${contactsRemaining <= 0 ? "bg-red-500" : "bg-emerald-500"}`}
                  style={{ width: `${Math.min(100, (contactsUsed / contactsLimit) * 100)}%` }} />
              </div>
              {contactsRemaining <= 2 && contactsRemaining > 0 && (
                <p className="text-xs text-amber-400 font-body mt-1">{t("hd.free_contacts_warning")}</p>
              )}
              {contactsRemaining <= 0 && (
                <p className="text-xs text-red-400 font-body mt-1">{t("hd.free_contacts_exhausted_sidebar")}</p>
              )}
            </div>
          </div>
        )}
        {/* Contact usage indicator */}
        {hasActivePlan && planStatus.contacts_limit > 0 && (
          <div className="px-4 py-2">
            <div className="bg-navy-800 rounded-lg p-3">
              <div className="flex justify-between text-xs text-slate-400 font-body mb-1">
                <span>{planStatus.contacts_used}/{planStatus.contacts_limit} {t("hd.contacts_used")}</span>
              </div>
              <div className="w-full bg-navy-700 rounded-full h-1.5">
                <div className="bg-gold-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (planStatus.contacts_used / planStatus.contacts_limit) * 100)}%` }} />
              </div>
            </div>
          </div>
        )}
        {hasActivePlan && planStatus.contacts_limit === -1 && (
          <div className="px-4 py-2">
            <div className="bg-navy-800 rounded-lg p-2 text-center">
              <span className="text-xs text-gold-400 font-body">{t("hd.contacts_unlimited")}</span>
            </div>
          </div>
        )}
        <nav className="flex-1 p-4 space-y-1">
          {NAV_ITEMS.map(item => (
            <button key={item.id} className={`sidebar-nav-item w-full text-left ${activeTab === item.id ? "active" : ""} ${item.locked ? "opacity-60" : ""}`}
              onClick={() => setActiveTab(item.id)} data-testid={`nav-${item.id}`}>
              <item.icon className="w-5 h-5" /> {item.label}
              {item.locked && <Lock className="w-3 h-3 ml-auto text-slate-500" />}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-navy-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-navy-700 flex items-center justify-center">
              <span className="text-white text-xs font-bold">{(hotel?.name || "H")[0]}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-sans truncate">{hotel?.name || "Hotel"}</p>
              <p className="text-xs text-slate-400 font-body capitalize">{isFree ? t("hd.free_badge") : (hotel?.subscription_plan || "-")}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" className="w-full text-slate-400 hover:text-white justify-start" onClick={logout} data-testid="hotel-logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
          </Button>
          <Link to="/privacy" target="_blank" className="flex items-center gap-2 text-xs text-slate-500 hover:text-gold-400 font-body mt-2 px-2 transition-colors" data-testid="hotel-privacy-link">
            <Shield className="w-3 h-3" /> {t("auth.privacy_link")}
          </Link>
          <Link to="/terms" target="_blank" className="flex items-center gap-2 text-xs text-slate-500 hover:text-gold-400 font-body mt-1 px-2 transition-colors" data-testid="hotel-terms-link">
            <FileText className="w-3 h-3" /> {t("tos.link")}
          </Link>
          <Link to="/hotel-terms" target="_blank" className="flex items-center gap-2 text-xs text-slate-500 hover:text-gold-400 font-body mt-1 px-2 transition-colors" data-testid="hotel-terms-use-link">
            <Building2 className="w-3 h-3" /> {t("htos.link")}
          </Link>
          <button onClick={() => setShowDeleteDialog(true)} className="flex items-center gap-2 text-xs text-red-400 hover:text-red-500 font-body mt-1 px-2 transition-colors" data-testid="hotel-open-delete-btn">
            <Trash2 className="w-3 h-3" /> {t("account.delete_title")}
          </button>
        </div>
      </aside>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-navy-900 border-t border-navy-800 z-[70] flex safe-bottom">
        {NAV_ITEMS.slice(0, 4).map(item => (
          <button key={item.id} className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-body ${activeTab === item.id ? "text-gold-500" : "text-slate-400"}`}
            onClick={() => setActiveTab(item.id)} data-testid={`mob-nav-${item.id}`}>
            <item.icon className="w-5 h-5" />{item.label.split(" ")[0]}
          </button>
        ))}
        <button className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 text-[10px] font-body ${["matching","subscription","settings"].includes(activeTab) ? "text-gold-500" : "text-slate-400"}`}
          onClick={() => setShowMobileMore(!showMobileMore)} data-testid="mob-nav-more">
          <Menu className="w-5 h-5" />{t("common.more") || "More"}
        </button>
      </div>
      {showMobileMore && (
        <>
          <div className="lg:hidden fixed inset-0 z-40" onClick={() => setShowMobileMore(false)} />
          <div className="lg:hidden fixed bottom-14 right-2 bg-navy-900 border border-navy-700 rounded-lg shadow-xl z-[70] py-2 min-w-[180px]" data-testid="mobile-more-menu">
            {NAV_ITEMS.slice(4).map(item => (
              <button key={item.id} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body ${activeTab === item.id ? "text-gold-500" : "text-slate-300"} hover:bg-navy-800`}
                onClick={() => { setActiveTab(item.id); setShowMobileMore(false); }}>
                <item.icon className="w-4 h-4" /> {item.label}
              </button>
            ))}
            <div className="border-t border-navy-700 mt-1 pt-1">
              <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-body text-red-400 hover:bg-navy-800"
                onClick={logout}>
                <LogOut className="w-4 h-4" /> {t("nav.logout")}
              </button>
            </div>
          </div>
        </>
      )}

      <main className="flex-1 p-6 md:p-8 pb-24 lg:pb-8 overflow-auto">
        {/* Free plan banner */}
        {isFree && activeTab !== "subscription" && (
          <div className="mb-6" data-testid="free-plan-banner">
            <Card className="bg-gradient-to-r from-navy-900 to-navy-800 border-gold-500/30">
              <CardContent className="flex items-center justify-between gap-4 py-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                    <Crown className="w-5 h-5 text-gold-400" />
                  </div>
                  <div>
                    <p className="font-sans font-semibold text-white text-sm">{t("hd.free_banner")}</p>
                    <p className="text-xs text-gold-400 font-body mt-0.5">{t("hd.free_banner_cta")}</p>
                  </div>
                </div>
                <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm whitespace-nowrap" onClick={goToSubscription} data-testid="free-subscribe-btn">
                  <Crown className="w-4 h-4 mr-2" />{t("hd.demo_popup_cta")}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Verification pending banner */}
        {!isVerified && activeTab !== "subscription" && (
          <div className="mb-6" data-testid="verification-pending-banner">
            <Card className={`border ${verificationStatus === "rejected" ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
              <CardContent className="flex items-center gap-4 py-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${verificationStatus === "rejected" ? "bg-red-100" : "bg-amber-100"}`}>
                  {verificationStatus === "rejected" ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <Clock className="w-5 h-5 text-amber-600" />}
                </div>
                <div>
                  <p className={`font-sans font-semibold text-sm ${verificationStatus === "rejected" ? "text-red-900" : "text-amber-900"}`}>
                    {verificationStatus === "rejected" ? t("verif.rejected") : verificationStatus === "flagged" ? t("verif.flagged") : t("verif.pending")}
                  </p>
                  <p className={`text-xs font-body mt-0.5 ${verificationStatus === "rejected" ? "text-red-700" : "text-amber-700"}`}>
                    {verificationStatus === "rejected" ? t("verif.rejected_desc") : verificationStatus === "flagged" ? t("verif.flagged_desc") : t("verif.pending_desc")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {/* Active plan but expired */}
        {!isFree && !hasActivePlan && activeTab !== "subscription" && (
          <div className="mb-6">
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="flex items-center justify-between gap-4 py-4">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-sans font-semibold text-amber-900">{t("hd.plan_required")}</p>
                    <p className="text-sm text-amber-700 font-body">{t("hd.plan_required_desc")}</p>
                  </div>
                </div>
                <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm" onClick={goToSubscription} data-testid="subscribe-prompt-btn">
                  {t("hd.subscribe")}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "search" && (
          !isVerified ? (
            <div className="text-center py-16" data-testid="verification-block">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="font-sans font-bold text-xl text-slate-900 mb-2">{t("verif.access_blocked_title")}</h2>
              <p className="text-sm text-slate-500 font-body max-w-md mx-auto">{t("verif.access_blocked_desc")}</p>
            </div>
          ) : (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-sans font-bold text-2xl text-slate-900">{t("hd.search")}</h1>
              <Badge className="bg-gold-500/10 text-gold-600 border-gold-500/20 font-body">{totalResults} {t("hd.results")}</Badge>
            </div>
            <Card className="bg-white border-slate-200 shadow-sm mb-6">
              <CardContent className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <div className="relative">
                    <Select value={filters.position} onValueChange={(v) => setFilters(p => ({ ...p, position: v }))}>
                      <SelectTrigger className="h-11 font-body" data-testid="filter-position"><SelectValue placeholder={t("hd.position")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("hd.all_positions")}</SelectItem>
                        {POSITIONS_LIST.map(p => <SelectItem key={p} value={p}>{tp(p)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <Input placeholder={t("hd.location")} value={filters.location} onChange={(e) => setFilters(p => ({ ...p, location: e.target.value }))}
                      className={`h-11 font-body ${isFree ? "opacity-50" : ""}`} disabled={isFree} data-testid="filter-location" />
                    {isFree && <Lock className="absolute right-3 top-3.5 w-3.5 h-3.5 text-slate-400" />}
                  </div>
                  <Select value={filters.country} onValueChange={(v) => setFilters(p => ({ ...p, country: v }))}>
                    <SelectTrigger className="h-11 font-body" data-testid="filter-country">
                      <SelectValue placeholder={t("hd.country")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("hd.all_countries")}</SelectItem>
                      {EUROPEAN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{COUNTRY_FLAGS[c]} {tc(c)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="relative">
                    <Input type="number" placeholder={t("hd.experience_min")} value={filters.experience_min}
                      onChange={(e) => setFilters(p => ({ ...p, experience_min: e.target.value }))}
                      className={`h-11 font-body ${!features.search_advanced ? "opacity-50" : ""}`}
                      disabled={!features.search_advanced} data-testid="filter-experience" />
                    {!features.search_advanced && <Lock className="absolute right-3 top-3.5 w-3.5 h-3.5 text-slate-400" />}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <Select value={filters.preferred_country} onValueChange={(v) => setFilters(p => ({ ...p, preferred_country: v }))}>
                    <SelectTrigger className="h-9 font-body w-auto min-w-[220px] text-sm" data-testid="filter-preferred-country">
                      <SelectValue placeholder={t("hd.filter_preferred_country")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("hd.all_preferred")}</SelectItem>
                      {PRIORITY_COUNTRIES.map(c => <SelectItem key={c} value={c}><span className="mr-1.5">{COUNTRY_FLAGS[c]}</span>{tc(c)}</SelectItem>)}
                      <Separator className="my-1" />
                      {EUROPEAN_COUNTRIES.filter(c => !PRIORITY_COUNTRIES.includes(c)).map(c => <SelectItem key={c} value={c}><span className="mr-1.5">{COUNTRY_FLAGS[c]}</span>{tc(c)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Switch checked={filters.available_now} onCheckedChange={(v) => setFilters(p => ({ ...p, available_now: v }))}
                      disabled={!features.search_advanced} data-testid="filter-available-now" />
                    <span className={`text-sm font-body ${!features.search_advanced ? "text-slate-400" : "text-slate-600"}`}>
                      {t("hd.available_only")} {!features.search_advanced && <Lock className="w-3 h-3 inline ml-1" />}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch checked={filters.needs_accommodation} onCheckedChange={(v) => setFilters(p => ({ ...p, needs_accommodation: v }))}
                      disabled={!features.search_advanced} data-testid="filter-needs-accommodation" />
                    <span className={`text-sm font-body ${!features.search_advanced ? "text-slate-400" : "text-slate-600"}`}>
                      <Home className="w-3 h-3 inline mr-1" />{t("cd.needs_accommodation")} {!features.search_advanced && <Lock className="w-3 h-3 inline ml-1" />}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-3.5 h-3.5 text-slate-500" />
                    <span className={`text-sm font-body ${!features.search_advanced ? "text-slate-400" : "text-slate-600"}`}>{t("hd.available_from")}:</span>
                    <Input type="date" value={filters.available_from}
                      onChange={(e) => setFilters(p => ({ ...p, available_from: e.target.value }))}
                      className={`h-8 w-40 text-sm font-body ${!features.search_advanced ? "opacity-50" : ""}`}
                      disabled={!features.search_advanced} data-testid="filter-available-from" />
                  </div>
                </div>
                {/* Distance filter section */}
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid="distance-filter-section">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={distanceActive} onCheckedChange={setDistanceActive} data-testid="distance-toggle" />
                      <span className="text-sm font-body font-semibold text-slate-700 flex items-center gap-1.5">
                        <Navigation className="w-3.5 h-3.5 text-gold-500" /> {t("dist.title")}
                      </span>
                    </div>
                    {distanceActive && distanceFrom && (
                      <button className="text-xs text-slate-400 hover:text-slate-600 font-body" onClick={() => { setDistanceActive(false); setDistanceFrom(""); setDistanceCoords(null); setSelectedLocation(""); setGeoSuggestions([]); }} data-testid="distance-clear">
                        {t("dist.clear")}
                      </button>
                    )}
                  </div>
                  {distanceActive && (
                    <div className="space-y-3 mt-3">
                      <div className="relative">
                        <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                        <Input
                          placeholder={t("dist.from_placeholder")}
                          value={distanceFrom}
                          onChange={(e) => handleDistanceFromChange(e.target.value)}
                          className="h-10 font-body pl-9 text-sm"
                          data-testid="distance-from-input"
                        />
                        {geoSuggestions.length > 0 && (
                          <div className="absolute z-20 top-11 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                            {geoSuggestions.map((s) => (
                              <button key={s.display} className="w-full text-left px-3 py-2 text-sm font-body hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-0"
                                onClick={() => { setDistanceFrom(s.display); setDistanceCoords({lat: s.lat, lng: s.lng}); setSelectedLocation(s.display); setGeoSuggestions([]); }}
                                data-testid={`geo-suggestion-${s.display}`}>
                                <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                <span className="truncate">{s.display}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 font-body w-16 flex-shrink-0">{t("dist.radius")}:</span>
                        <Slider
                          value={[distanceKm]}
                          onValueChange={([v]) => setDistanceKm(v)}
                          min={10} max={500} step={10}
                          className="flex-1"
                          data-testid="distance-slider"
                        />
                        <span className="text-sm font-semibold text-navy-900 font-body w-16 text-right">{distanceKm} {t("dist.km")}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm ml-auto" onClick={() => searchCandidates()}
                    disabled={searchLoading} data-testid="search-btn">
                    {searchLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />} {t("hd.search_btn")}
                  </Button>
                </div>
                {!features.search_advanced && hasActivePlan && (
                  <p className="text-xs text-amber-600 font-body mt-2 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> {t("hd.advanced_filters_locked")}
                  </p>
                )}
              </CardContent>
            </Card>
            <div className="space-y-3">
              {/* Results header */}
              {hasSearched && (
                <div className="flex items-center justify-between" data-testid="results-header">
                  <p className="text-sm text-slate-500 font-body">
                    {t("hd.showing")} <span className="font-semibold text-slate-700">{candidates.length}</span> {t("hd.of")} <span className="font-semibold text-slate-700">{totalResults}</span> {t("hd.candidates_label")}
                  </p>
                  {totalResults > 0 && <Badge className="bg-slate-100 text-slate-600 border-slate-200 font-body text-xs">{totalResults} {t("hd.total_results")}</Badge>}
                </div>
              )}
              {distanceActive && !distanceAccess && candidates.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3" data-testid="distance-restricted-banner">
                  <Lock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-700 font-body flex-1">{t("dist.restricted")}</p>
                  <Button size="sm" className="bg-gold-500 hover:bg-gold-600 text-white font-body text-xs rounded-sm flex-shrink-0" onClick={goToSubscription}>
                    <Crown className="w-3 h-3 mr-1" /> Upgrade
                  </Button>
                </div>
              )}
              {/* Loading skeleton */}
              {searchLoading && candidates.length === 0 && (
                <div className="space-y-3" data-testid="search-skeleton">
                  {[1,2,3,4].map(i => (
                    <Card key={i} className="bg-white border-slate-200 shadow-sm animate-pulse">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-full bg-slate-200" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-slate-200 rounded w-1/3" />
                            <div className="h-3 bg-slate-100 rounded w-2/3" />
                            <div className="h-3 bg-slate-100 rounded w-1/2" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              {candidates.map(c => (
                <CandidateCard key={c.user_id} candidate={c} isFavorite={favoriteIds.has(c.user_id)}
                  canFavorite={!!features.favorites} canViewCV={isFree ? freeCanViewCV : !!features.view_cv}
                  canMessage={!!features.messaging} profileViewsRemaining={profileViewsRemaining}
                  contactsRemaining={contactsRemaining}
                  isFree={isFree} onFreeClick={() => handleBlockedAction(t("hd.free_view_profile"))}
                  onContactsExhausted={() => setShowUpgradePopup(true)}
                  onFavorite={handleFavorite} onRemoveFavorite={handleRemoveFavorite} onHire={handleHire} onMessage={handleMessage}
                  onProfileViewed={loadPlanStatus} onUpgrade={goToSubscription} t={t} tp={tp} tc={tc} tl={tl} />
              ))}
              {/* Load more button */}
              {candidates.length > 0 && candidates.length < totalResults && (
                <div className="text-center pt-4 pb-2">
                  <Button variant="outline" className="border-slate-300 text-slate-600 font-body rounded-sm px-8"
                    onClick={() => searchCandidates(true)} disabled={searchLoading} data-testid="load-more-btn">
                    {searchLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t("hd.load_more")} ({candidates.length}/{totalResults})
                  </Button>
                </div>
              )}
              {/* Empty state - only when no results after search */}
              {candidates.length === 0 && !searchLoading && hasSearched && (
                <div className="text-center py-12 text-slate-400">
                  <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-body">{t("hd.no_results")}</p>
                </div>
              )}
            </div>
          </div>
          )
        )}

        {activeTab === "favorites" && (
          !isVerified ? (
            <div className="text-center py-16" data-testid="verification-block-favorites">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="font-sans font-bold text-xl text-slate-900 mb-2">{t("verif.access_blocked_title")}</h2>
              <p className="text-sm text-slate-500 font-body max-w-md mx-auto">{t("verif.access_blocked_desc")}</p>
            </div>
          ) :
          features.favorites ? (
            <div>
              <h1 className="font-sans font-bold text-2xl text-slate-900 mb-6">{t("hd.favorites")} <Badge className="ml-2 font-body">{favorites.length}</Badge></h1>
              <div className="space-y-3">
                {favorites.map(f => <CandidateCard key={f.favorite_id} candidate={f.candidate} isFavorite={true}
                  canFavorite={true} canViewCV={!!features.view_cv} canMessage={!!features.messaging}
                  onFavorite={handleFavorite} onRemoveFavorite={handleRemoveFavorite} onHire={handleHire} onMessage={handleMessage} t={t} tp={tp} tc={tc} tl={tl} />)}
                {favorites.length === 0 && (
                  <div className="text-center py-12 text-slate-400"><Heart className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="font-body">{t("hd.no_favorites")}</p></div>
                )}
              </div>
            </div>
          ) : (
            <LockedFeature icon={Heart} title={t("hd.favorites")} description={t("hd.favorites_locked")} onUpgrade={goToSubscription} t={t} />
          )
        )}

        {activeTab === "hired" && (
          <div>
            <h1 className="font-sans font-bold text-2xl text-slate-900 mb-6">{t("hd.hired")} <Badge className="ml-2 font-body">{hires.length}</Badge></h1>
            <div className="space-y-3">
              {hires.map(h => (
                <Card key={h.hire_id} className="bg-white border-slate-200 shadow-sm" data-testid={`hire-${h.hire_id}`}>
                  <CardContent className="p-5 flex items-center justify-between">
                    <div>
                      <p className="font-sans font-semibold text-slate-900">{h.candidate_name || "?"}</p>
                      <div className="flex items-center gap-3 text-sm text-slate-500 font-body">
                        <span>{tp(h.position)}</span><span>{h.created_at?.split("T")[0]}</span>
                      </div>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 font-body text-xs">{h.status || "confirmed"}</Badge>
                  </CardContent>
                </Card>
              ))}
              {hires.length === 0 && (
                <div className="text-center py-12 text-slate-400"><UserCheck className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="font-body">{t("hd.no_hires")}</p></div>
              )}
            </div>
          </div>
        )}

        {activeTab === "matching" && (
          features.ai_matching ? (
            <div>
              <h1 className="font-sans font-bold text-2xl text-slate-900 mb-6"><Zap className="w-6 h-6 text-gold-500 inline mr-2" />{t("hd.ai_title")}</h1>
              <Card className="bg-white border-slate-200 shadow-sm mb-6">
                <CardContent className="p-5">
                  <p className="text-sm text-slate-500 font-body mb-4">{t("hd.ai_desc")}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {POSITIONS_LIST.map(pos => (
                      <Badge key={pos} variant={matchFilters.positions.includes(pos) ? "default" : "outline"}
                        className={`cursor-pointer font-body ${matchFilters.positions.includes(pos) ? "bg-gold-500 text-white border-gold-500" : "border-slate-300 text-slate-600"}`}
                        onClick={() => setMatchFilters(p => ({ ...p, positions: p.positions.includes(pos) ? p.positions.filter(x => x !== pos) : [...p.positions, pos] }))}
                        data-testid={`match-pos-${pos}`}>
                        {tp(pos)}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Switch checked={matchFilters.available_now} onCheckedChange={(v) => setMatchFilters(p => ({ ...p, available_now: v }))} />
                      <span className="text-sm text-slate-600 font-body">{t("hd.available_only")}</span>
                    </div>
                    <Button className="bg-gold-500 hover:bg-gold-600 text-white font-semibold rounded-sm ml-auto" onClick={runMatching} disabled={loading} data-testid="run-matching-btn">
                      {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />} {t("hd.ai_run")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              {matchResults?.ai_analysis && (
                <Card className="bg-navy-900 border-navy-800 text-white mb-6">
                  <CardContent className="p-5">
                    <p className="text-sm font-body text-slate-300"><Zap className="w-4 h-4 text-gold-500 inline mr-1" /> <strong className="text-gold-400">{t("hd.ai_analysis")}</strong> {matchResults.ai_analysis}</p>
                  </CardContent>
                </Card>
              )}
              <div className="space-y-3">
                {(matchResults?.recommendations || []).map(c => (
                  <CandidateCard key={c.user_id} candidate={c} isFavorite={favoriteIds.has(c.user_id)}
                    canFavorite={!!features.favorites} canViewCV={!!features.view_cv} canMessage={!!features.messaging}
                    onFavorite={handleFavorite} onRemoveFavorite={handleRemoveFavorite} onHire={handleHire} onMessage={handleMessage} t={t} tp={tp} tc={tc} tl={tl} />
                ))}
              </div>
            </div>
          ) : (
            <LockedFeature icon={Zap} title={t("hd.ai_title")} description={t("hd.ai_locked")} onUpgrade={goToSubscription} t={t} />
          )
        )}

        {activeTab === "jobs" && (
          <JobPostingsView hotel={hotel} t={t} tc={tc} tp={tp} />
        )}

        {activeTab === "messages" && (
          features.messaging ? (
            <div>
              <h2 className="font-sans text-xl font-bold text-slate-900 mb-5">{t("msg.title")}</h2>
              <MessagesView user={user} t={t} />
            </div>
          ) : (
            <LockedFeature icon={MessageSquare} title={t("msg.title")} description={t("hd.free_messaging_locked")} onUpgrade={goToSubscription} t={t} />
          )
        )}

        {activeTab === "subscription" && (
          <div>
            <h1 className="font-sans font-bold text-2xl text-slate-900 mb-6">{t("hd.my_subscription")}</h1>
            {/* Current Free Plan info */}
            {isFree && (
              <Card className="bg-white border-2 border-amber-300 shadow-md mb-6" data-testid="free-plan-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Crown className="w-6 h-6 text-amber-500" />
                    <h2 className="font-sans font-bold text-xl text-slate-900">{t("hd.free_plan_title")}</h2>
                  </div>
                  <Badge className="bg-amber-50 text-amber-700 font-body mb-4">{t("hd.active")}</Badge>
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-body"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-slate-700">{t("hd.free_f1")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-slate-700">{t("hd.free_f2")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><CheckCircle2 className="w-4 h-4 text-emerald-500" /><span className="text-slate-700">{t("hd.free_f3")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><Lock className="w-4 h-4 text-slate-300" /><span className="text-slate-400 line-through">{t("hd.free_r1")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><Lock className="w-4 h-4 text-slate-300" /><span className="text-slate-400 line-through">{t("hd.free_r2")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><Lock className="w-4 h-4 text-slate-300" /><span className="text-slate-400 line-through">{t("hd.free_r3")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><Lock className="w-4 h-4 text-slate-300" /><span className="text-slate-400 line-through">{t("hd.free_r4")}</span></div>
                    <div className="flex items-center gap-2 text-sm font-body"><Lock className="w-4 h-4 text-slate-300" /><span className="text-slate-400 line-through">{t("hd.free_r5")}</span></div>
                  </div>
                  <div className="mt-4 bg-amber-50 rounded-lg p-3">
                    <p className="text-sm text-amber-700 font-body">{t("hd.free_views_status", { used: profileViewsUsed, limit: profileViewsLimit })}</p>
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Active paid plan */}
            {hasActivePlan && hotel?.subscription_status === "active" && (
              <Card className="bg-white border-2 border-gold-500 shadow-md mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Crown className="w-6 h-6 text-gold-500" />
                    <h2 className="font-sans font-bold text-xl text-slate-900 capitalize">Plan {hotel.subscription_plan}</h2>
                    {isFounder && <Badge className="bg-amber-100 text-amber-800 border-amber-300 font-body" data-testid="founder-badge-sub">{t("hd.founder_badge")}</Badge>}
                  </div>
                  <Badge className="bg-emerald-50 text-emerald-700 font-body mb-4">{t("hd.active")}</Badge>
                  {isFounder && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm text-amber-700 font-body">{t("hd.founder_lifetime_msg", { price: planStatus.founder_price || 99 })}</p>
                    </div>
                  )}
                  {planStatus && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      {Object.entries(features).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-2 text-sm font-body">
                          <CheckCircle2 className={`w-4 h-4 ${val ? "text-emerald-500" : "text-slate-300"}`} />
                          <span className={val ? "text-slate-700" : "text-slate-400 line-through"}>
                            {key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Upgrade to Premium - shown for free plan */}
            {(isFree || !hasActivePlan) && (
              <div>
                <h2 className="font-sans font-bold text-lg text-slate-900 mb-4">{t("hd.free_upgrade_title")}</h2>
                <Card className="border-2 border-gold-500 shadow-lg">
                  <CardContent className="p-6">
                    <Badge className="mb-3 bg-gold-500 text-white font-body">{t("price.recommended")}</Badge>
                    <h3 className="font-sans font-bold text-xl text-slate-900 mb-1">Premium</h3>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-sans font-bold text-3xl text-slate-900">99</span>
                      <span className="text-sm text-slate-500 font-body">EUR{t("price.month")}</span>
                    </div>
                    <p className="text-sm text-slate-500 font-body mb-4">{t("price.premium_desc")}</p>
                    <ul className="space-y-2 mb-6">
                      {["price.prem.f1","price.prem.f2","price.prem.f3","price.prem.f4","price.prem.f5","price.prem.f6","price.prem.f7"].map(fk => (
                        <li key={fk} className="flex items-start gap-2 text-sm text-slate-600 font-body">
                          <CheckCircle2 className="w-4 h-4 text-gold-500 mt-0.5 flex-shrink-0" />{t(fk)}
                        </li>
                      ))}
                    </ul>
                    <Button className="w-full rounded-sm font-semibold bg-gold-500 hover:bg-gold-600 text-white"
                      onClick={() => handleSubscription("premium")} data-testid="subscribe-premium">
                      {t("hd.subscribe")}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}

        {activeTab === "settings" && (
          <SettingsView user={user} onLogout={logout} onPremium={() => setShowUpgradePopup(true)} />
        )}
      </main>

      {/* Upgrade Popup - Free plan */}
      <Dialog open={showUpgradePopup} onOpenChange={setShowUpgradePopup}>
        <DialogContent className="sm:max-w-sm p-0 overflow-hidden border-0 rounded-lg shadow-2xl" data-testid="coming-soon-dialog">
          <DialogTitle className="sr-only">{t("premium.coming_soon_title")}</DialogTitle>
          <div className="bg-navy-900 px-6 pt-7 pb-5 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gold-500/20 flex items-center justify-center">
              <Crown className="w-7 h-7 text-gold-400" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white mb-1">{t("premium.coming_soon_title")}</h2>
            <p className="text-sm text-slate-300 font-body">{t("premium.coming_soon_desc")}</p>
          </div>
          <div className="px-6 py-5">
            <Button variant="outline" className="w-full" onClick={() => setShowUpgradePopup(false)} data-testid="coming-soon-close-btn">
              {t("common.close")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={(v) => { setShowDeleteDialog(v); if (!v) setDeleteConfirm(""); }}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0" data-testid="hotel-delete-account-dialog">
          <div className="bg-red-600 px-6 pt-6 pb-4 text-center">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-white" />
            </div>
            <h2 className="font-sans text-lg font-bold text-white">{t("account.delete_title")}</h2>
          </div>
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-slate-600 font-body leading-relaxed">{t("account.delete_desc")}</p>
            <div>
              <Label className="text-sm font-body text-slate-500 mb-1 block">{t("account.delete_confirm")}</Label>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder={t("account.delete_keyword")} className="h-10 font-body border-red-200 focus:border-red-400" data-testid="hotel-delete-confirm-input" />
            </div>
            <div className="flex gap-3 pt-1">
              <Button variant="outline" className="flex-1 font-body" onClick={() => { setShowDeleteDialog(false); setDeleteConfirm(""); }} data-testid="hotel-delete-cancel-btn">
                {t("common.cancel")}
              </Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold"
                disabled={deleteConfirm !== t("account.delete_keyword") || deleting}
                onClick={async () => {
                  setDeleting(true);
                  try { await authAPI.deleteAccount(); logout(); navigate("/", { replace: true }); }
                  catch { toast.error("Error"); setDeleting(false); }
                }} data-testid="hotel-delete-confirm-btn">
                <Trash2 className="w-4 h-4 mr-2" /> {deleting ? "..." : t("account.delete_btn")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
